import { useEffect, useMemo, useState } from "react";
import { useAdminData } from "../AdminDataContext";
import { activityStore } from "../utils/dataStore";
import { fetchMemberPointsBreakdown, type MemberPointsBreakdownRow } from "../../../api/memberPointsBreakdown";

type PanelTab = "leaderboard" | "distribution";

const LEVEL_COLORS: Record<string, string> = {
  高级会员: "bg-amber-50 text-amber-600",
  普通会员: "bg-gray-100 text-gray-600",
};

export default function ActivityLeaderboardPanel() {
  const { members, tenantLegacyId, storeVersion } = useAdminData();
  const [breakdownByMember, setBreakdownByMember] = useState<Record<string, MemberPointsBreakdownRow>>({});

  useEffect(() => {
    let cancelled = false;
    fetchMemberPointsBreakdown(tenantLegacyId)
      .then((rows) => {
        if (cancelled) return;
        const m: Record<string, MemberPointsBreakdownRow> = {};
        rows.forEach((r) => {
          m[r.memberLegacyId] = r;
        });
        setBreakdownByMember(m);
      })
      .catch(() => {
        if (!cancelled) setBreakdownByMember({});
      });
    return () => {
      cancelled = true;
    };
  }, [tenantLegacyId, storeVersion]);

  const ranked = useMemo(
    () => [...members].sort((a, b) => b.points - a.points),
    [members]
  );
  const [panelTab, setPanelTab] = useState<PanelTab>("leaderboard");
  const activities = activityStore.getAll();
  const pointsActivities = activities.filter((a) => (a.participationPoints ?? 0) > 0);
  const totalActivityPointsDistributed = pointsActivities.reduce(
    (sum, a) => sum + (a.participationPoints ?? 0) * a.enrolled,
    0
  );
  const totalMemberPoints = members.reduce((s, m) => s + m.points, 0);
  const activeMembers = members.filter((m) => m.status === "active").length;
  const n = Math.max(members.length, 1);

  return (
    <div className="border-b border-gray-100 bg-gradient-to-br from-amber-50/60 to-orange-50/40">
      {/* Panel Header */}
      <div className="px-6 pt-5 pb-0">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="text-sm font-bold text-gray-900 flex items-center gap-2">
              <i className="ri-bar-chart-grouped-line text-amber-500" />
              积分排行与会员联动
            </h4>
            <p className="text-xs text-gray-400 mt-0.5">
              排行数据来自会员表；报名次数与积分来源分布依据活动报名与积分流水汇总
            </p>
          </div>
          {/* Sub-tab toggle */}
          <div className="flex bg-white rounded-xl border border-gray-200 p-1 gap-1">
            {(
              [
                ["leaderboard", "会员积分排行", "ri-trophy-line"],
                ["distribution", "活动积分配置", "ri-coin-line"],
              ] as const
            ).map(([key, label, icon]) => (
              <button
                key={key}
                onClick={() => setPanelTab(key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer transition-all whitespace-nowrap ${
                  panelTab === key ? "bg-amber-500 text-white" : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <i className={`${icon} text-xs`} />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Overview Stats */}
        <div className="grid grid-cols-4 gap-3 mb-5">
          {[
            { label: "正式会员总数", value: members.length, sub: `${activeMembers} 活跃`, icon: "ri-group-line", color: "text-teal-600 bg-teal-50" },
            { label: "会员积分总量", value: totalMemberPoints.toLocaleString(), sub: "全部会员累计", icon: "ri-coin-line", color: "text-amber-600 bg-amber-50" },
            { label: "活动发放积分", value: totalActivityPointsDistributed.toLocaleString(), sub: `${pointsActivities.length} 个活动配置`, icon: "ri-calendar-check-line", color: "text-red-600 bg-red-50" },
            { label: "人均积分", value: Math.round(totalMemberPoints / n).toLocaleString(), sub: "当前平均水平", icon: "ri-user-star-line", color: "text-orange-600 bg-orange-50" },
          ].map((stat) => (
            <div key={stat.label} className="bg-white rounded-2xl px-4 py-3 flex items-center gap-3 border border-gray-100">
              <div className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 ${stat.color}`}>
                <i className={`${stat.icon} text-base`} />
              </div>
              <div>
                <p className="text-xs text-gray-400">{stat.label}</p>
                <p className="text-base font-bold text-gray-900 leading-tight">{stat.value}</p>
                <p className="text-[10px] text-gray-400">{stat.sub}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/*  ===== Leaderboard Tab ===== */}
      {panelTab === "leaderboard" && (
        <div className="px-6 pb-5">
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-3.5 border-b border-gray-50 flex items-center justify-between">
              <p className="text-sm font-bold text-gray-800">会员积分排行榜</p>
              <span className="text-xs text-gray-400">流水汇总 · {members.length} 名会员</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50/80">
                    {["排名", "会员", "等级", "活动报名", "积分获得分布", "总积分"].map((h) => (
                      <th key={h} className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {ranked.map((member, idx) => {
                    const rank = idx + 1;
                    const row = breakdownByMember[member.id];
                    const stat = row ?? {
                      memberLegacyId: member.id,
                      activityRegistrationCount: 0,
                      pointsActivity: 0,
                      pointsVote: 0,
                      pointsSignin: 0,
                      pointsOther: 0,
                    };
                    const total =
                      stat.pointsActivity + stat.pointsVote + stat.pointsSignin + stat.pointsOther;
                    const pct = (v: number) => (total > 0 ? Math.round((v / total) * 100) : 0);
                    const activityPct = pct(stat.pointsActivity);
                    const votePct = pct(stat.pointsVote);
                    const signinPct = pct(stat.pointsSignin);
                    const otherPct = pct(stat.pointsOther);
                    const isTop3 = rank <= 3;
                    return (
                      <tr key={member.id} className="hover:bg-gray-50/60 transition-colors">
                        <td className="px-4 py-3">
                          <div
                            className={`w-7 h-7 flex items-center justify-center rounded-full text-xs font-bold ${
                              rank === 1
                                ? "bg-amber-400 text-white"
                                : rank === 2
                                  ? "bg-gray-400 text-white"
                                  : rank === 3
                                    ? "bg-amber-700 text-white"
                                    : "bg-gray-100 text-gray-500"
                            }`}
                          >
                            {rank <= 3 ? (
                              <i className={`ri-${rank === 1 ? "vip-crown" : "medal"}-${rank === 1 ? "fill" : "line"} text-xs`} />
                            ) : (
                              rank
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 bg-gray-100 rounded-xl flex items-center justify-center text-xs font-bold text-gray-600 flex-shrink-0">
                              {member.realName.slice(0, 1)}
                            </div>
                            <div>
                              <p className="text-xs font-semibold text-gray-900">{member.realName}</p>
                              <p className="text-[10px] text-gray-400">{member.name}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`text-[10px] px-2 py-1 rounded-full font-medium ${LEVEL_COLORS[member.level] ?? "bg-gray-100 text-gray-500"}`}
                          >
                            {member.level}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            <span className={`text-sm font-bold ${isTop3 ? "text-amber-600" : "text-gray-700"}`}>
                              {stat.activityRegistrationCount}
                            </span>
                            <span className="text-xs text-gray-400">次</span>
                          </div>
                          <p className="text-[10px] text-gray-400 mt-0.5">活动侧报名记录</p>
                        </td>
                        <td className="px-4 py-3">
                          {total <= 0 ? (
                            <p className="text-[10px] text-gray-400">暂无正向积分流水</p>
                          ) : (
                            <>
                              <div className="flex h-2 w-40 rounded-full overflow-hidden gap-0.5">
                                <div className="bg-red-400 rounded-l-full" style={{ width: `${activityPct}%` }} title={`活动 ${activityPct}%`} />
                                <div className="bg-amber-400" style={{ width: `${votePct}%` }} title={`投票 ${votePct}%`} />
                                <div className="bg-green-400" style={{ width: `${signinPct}%` }} title={`签到 ${signinPct}%`} />
                                <div className="bg-slate-300 rounded-r-full" style={{ width: `${otherPct}%` }} title={`其他 ${otherPct}%`} />
                              </div>
                              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1">
                                <span className="text-[9px] text-red-500 flex items-center gap-0.5">
                                  <span className="w-1.5 h-1.5 bg-red-400 rounded-full inline-block" />
                                  活动 {stat.pointsActivity}
                                </span>
                                <span className="text-[9px] text-amber-500 flex items-center gap-0.5">
                                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full inline-block" />
                                  投票 {stat.pointsVote}
                                </span>
                                <span className="text-[9px] text-green-500 flex items-center gap-0.5">
                                  <span className="w-1.5 h-1.5 bg-green-400 rounded-full inline-block" />
                                  签到 {stat.pointsSignin}
                                </span>
                                <span className="text-[9px] text-slate-500 flex items-center gap-0.5">
                                  <span className="w-1.5 h-1.5 bg-slate-300 rounded-full inline-block" />
                                  其他 {stat.pointsOther}
                                </span>
                              </div>
                            </>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <p className={`text-sm font-bold ${isTop3 ? "text-amber-600" : "text-gray-800"}`}>
                            {member.points.toLocaleString()}
                          </p>
                          <p className="text-[10px] text-gray-400">积分</p>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ===== Activity Distribution Tab ===== */}
      {panelTab === "distribution" && (
        <div className="px-6 pb-5 space-y-4">
          <div className="flex items-center gap-4 text-xs text-gray-500 bg-white px-4 py-3 rounded-2xl border border-gray-100 flex-wrap">
            <span className="font-medium text-gray-700">积分来源说明：</span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 bg-red-400 rounded-full inline-block" />
              活动相关流水
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 bg-amber-400 rounded-full inline-block" />
              投票
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 bg-green-400 rounded-full inline-block" />
              签到
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 bg-slate-300 rounded-full inline-block" />
              其他（贡献等，不含兑换扣减）
            </span>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-3.5 border-b border-gray-50 flex items-center justify-between">
              <p className="text-sm font-bold text-gray-800">已配置积分的活动</p>
              <span className="text-xs text-gray-400">
                {pointsActivities.length} 个活动 / 共 {activities.length} 个
              </span>
            </div>
            {pointsActivities.length === 0 ? (
              <div className="py-10 text-center text-gray-400">
                <i className="ri-coin-line text-3xl text-gray-200 block mb-2" />
                <p className="text-sm">暂无配置积分的活动</p>
                <p className="text-xs mt-1 text-gray-300">创建活动时填写「参与积分」字段即可</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50/80">
                      {["活动名称", "活动日期", "状态", "单次积分", "报名人数", "已发放积分", "发放进度"].map((h) => (
                        <th key={h} className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 whitespace-nowrap">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {pointsActivities.map((a) => {
                      const distributed = (a.participationPoints ?? 0) * a.enrolled;
                      const pct = a.capacity > 0 ? Math.round((a.enrolled / a.capacity) * 100) : 0;
                      const statusMap: Record<string, { label: string; cls: string }> = {
                        ongoing: { label: "报名中", cls: "bg-green-50 text-green-600" },
                        full: { label: "已报满", cls: "bg-red-50 text-red-600" },
                        ended: { label: "已结束", cls: "bg-gray-100 text-gray-500" },
                        draft: { label: "草稿", cls: "bg-gray-100 text-gray-400" },
                        pending: { label: "待审核", cls: "bg-amber-50 text-amber-600" },
                      };
                      const sc = statusMap[a.status] ?? { label: a.status, cls: "bg-gray-100 text-gray-500" };
                      return (
                        <tr key={a.id} className="hover:bg-amber-50/30 transition-colors">
                          <td className="px-4 py-3">
                            <p className="text-xs font-semibold text-gray-900 max-w-[180px] line-clamp-1">{a.title}</p>
                            <p className="text-[10px] text-gray-400 mt-0.5">{a.activityType}</p>
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{a.date}</td>
                          <td className="px-4 py-3">
                            <span className={`text-[10px] px-2 py-1 rounded-full font-medium ${sc.cls}`}>{sc.label}</span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <i className="ri-coin-line text-amber-500 text-xs" />
                              <span className="text-sm font-bold text-amber-600">+{a.participationPoints}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">
                            {a.enrolled} / {a.capacity} 人
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-sm font-bold text-gray-800">{distributed.toLocaleString()}</p>
                            <p className="text-[10px] text-gray-400">积分已发</p>
                          </td>
                          <td className="px-4 py-3">
                            <div className="w-24">
                              <div className="flex items-center justify-between text-[10px] text-gray-400 mb-1">
                                <span>报名率</span>
                                <span>{pct}%</span>
                              </div>
                              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${pct >= 90 ? "bg-red-500" : pct >= 60 ? "bg-amber-400" : "bg-green-500"}`}
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {activities.filter((a) => !a.participationPoints || a.participationPoints === 0).length > 0 && (
            <div className="bg-amber-50 border border-amber-100 rounded-2xl px-5 py-4">
              <div className="flex items-start gap-3">
                <i className="ri-error-warning-line text-amber-500 text-base mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs font-bold text-amber-800 mb-1">
                    还有 {activities.filter((a) => !a.participationPoints || a.participationPoints === 0).length} 个活动未配置积分
                  </p>
                  <p className="text-[11px] text-amber-700 leading-relaxed mb-2">以下活动当前未配置参与积分，配置后会员签到参与时将自动发放：</p>
                  <div className="flex flex-wrap gap-2">
                    {activities
                      .filter((a) => !a.participationPoints || a.participationPoints === 0)
                      .slice(0, 5)
                      .map((a) => (
                        <span
                          key={a.id}
                          className="text-[10px] bg-white border border-amber-200 text-amber-700 px-2.5 py-1 rounded-xl font-medium"
                        >
                          {a.title.length > 15 ? `${a.title.slice(0, 15)}…` : a.title}
                        </span>
                      ))}
                  </div>
                  <p className="text-[11px] text-amber-600 mt-2 flex items-center gap-1">
                    <i className="ri-arrow-right-line" />
                    点击活动列表中的「编辑」按钮可配置积分
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
